"use client";

import { useAccount, useLogout, useModal, useWallet } from "@getpara/react-sdk";
import { ChainType } from "@lifi/sdk";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { AlertTriangle, LogOutIcon, Wallet, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useAccount as useWagmiAccount } from "wagmi";
import { createPortal } from "react-dom";
import { useTokenBalances, evmFetchSingleton } from "@/hooks/use-token-balances";
import { useTokenStore } from "@/lib/zustand";
import { useQueryClient } from "@tanstack/react-query";
import { usePortfolioStore } from "./mobile-portfolio-toggle";
import { useBalanceSyncStore } from "@/lib/balance-sync-store";
import { useOnChainBalanceOverrides } from "@/lib/on-chain-balance-overrides";

export function ConnectButton() {
  const { openModal } = useModal();
  const { data: wallet } = useWallet();
  const { isConnected } = useAccount();
  const { logoutAsync } = useLogout();
  const { fromChain } = useTokenStore();
  const { publicKey: solanaPublicKey, disconnect: disconnectSolana } =
    useSolanaWallet();
  const { address: wagmiAddress } = useWagmiAccount();
  const isSolana = fromChain.chainType === ChainType.SVM;
  const hasParaSession = isConnected && !!wallet?.address;
  const isSolanaWalletType = wallet?.type === "SOLANA";
  const isEvmConnected = isConnected && !!wallet?.address && wallet?.type === "EVM";
  const isSolanaConnected = !!solanaPublicKey || (hasParaSession && isSolanaWalletType);
  const { setIsOpen } = usePortfolioStore();
  const { availableTokens } = useTokenBalances();
  const isSyncing = useBalanceSyncStore((s) => s.isSyncing);
  const queryClient = useQueryClient();
  const [warningDismissed, setWarningDismissed] = useState(false);

  const displayAddress = isSolana
    ? (solanaPublicKey?.toBase58() ??
      (wallet?.type === "SOLANA" ? (wallet?.address ?? null) : null))
    : (wallet?.type === "EVM" ? (wallet?.address ?? null) : null) ?? wagmiAddress ?? null;

  const totalPortfolioValue = useMemo(
    () =>
      availableTokens
        .filter((token) => Number(token.balance) > 0)
        .reduce((sum, token) => sum + token.balanceUSD, 0),
    [availableTokens],
  );

  const formattedPortfolioValue = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(totalPortfolioValue),
    [totalPortfolioValue],
  );

  // Show warning for Para email/social wallets with zero balance
  // This catches: first-time signups, accidental email capitalization, or any case mismatch
  // Better to warn too much than miss a user who can't find their funds
  const showNewWalletWarning = useMemo(() => {
    // Only applies to Para email/social logins (not external wallets like Phantom)
    const walletEmail = (wallet as any)?.email;
    const walletUserId = (wallet as any)?.userId;
    const walletExternalId = (wallet as any)?.externalId;
    const isEmailOrSocialWallet = !!(walletEmail || walletUserId || walletExternalId);

    const shouldShow = isEmailOrSocialWallet && totalPortfolioValue === 0;

    if (process.env.NODE_ENV !== "production") {
      console.log("[Para Warning Check]", {
        walletType: wallet?.type,
        walletEmail,
        walletUserId,
        walletExternalId,
        isEmailOrSocialWallet,
        totalPortfolioValue,
        availableTokensCount: availableTokens?.length,
        shouldShow,
      });
    }

    if (!isEmailOrSocialWallet) return false;

    // Show warning if zero balance - user may have wrong email case
    // The warning message tells them to try lowercase email
    return totalPortfolioValue === 0;
  }, [wallet, totalPortfolioValue, availableTokens?.length]);

  const handleLogout = async () => {
    // Clear all balance caches to prevent cross-wallet data leakage.
    // Para SDK's profileBalance query key doesn't include wallet address,
    // so without clearing, the next user could see the previous user's cached balance.

    // Clear our zustand overrides
    useOnChainBalanceOverrides.getState().clearAll();

    // Clear the EVM fetch singleton (module-level cache)
    evmFetchSingleton.cachedResult = null;
    evmFetchSingleton.lastFetchKey = "";

    // Clear React Query caches related to balance
    queryClient.removeQueries({ queryKey: ["useProfileBalance"] });
    queryClient.removeQueries({ queryKey: ["balance"] });
    queryClient.removeQueries({ queryKey: ["profileBalance"] });

    // Disconnect Solana
    if (solanaPublicKey) await disconnectSolana();

    // Para logout
    await logoutAsync();

  };

  const isWagmiEvmConnected = !!wagmiAddress && !isSolana && !solanaPublicKey;

  const showConnectedState = isSolana
    ? isSolanaConnected
    : isEvmConnected || isWagmiEvmConnected;

  if (process.env.NODE_ENV !== "production") {
    console.log("[Para Connect State]", {
      isSolana,
      isSolanaConnected,
      isEvmConnected,
      isWagmiEvmConnected,
      showConnectedState,
      showNewWalletWarning,
    });
  }

  // Render warning banner via portal to document.body to escape any parent container issues
  const shouldShowWarning = showNewWalletWarning && !warningDismissed;
  const warningBanner = shouldShowWarning && typeof document !== 'undefined' ? createPortal(
    <div
      style={{
        position: 'fixed',
        top: '60px',
        left: 0,
        right: 0,
        zIndex: 99999,
      }}
    >
      <div className="flex items-center justify-between p-3 bg-amber-500 text-black">
        <div className="flex items-center gap-2 flex-1">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <p className="text-xs sm:text-sm">
            Can&apos;t find your funds? Para email login is case-sensitive. If you have an existing account, check your email capitalization matches exactly.
          </p>
        </div>
        <button
          onClick={() => setWarningDismissed(true)}
          className="ml-2 p-1 hover:bg-amber-600 rounded text-lg font-bold"
          aria-label="Dismiss warning"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>,
    document.body
  ) : null;

  if (showConnectedState) {
    return (
      <>
        {warningBanner}
        <div className="flex w-full items-center gap-1 sm:gap-2 min-w-0">
        <button
          onClick={() => openModal()}
          className="text-xs sm:text-base md:text-xl px-3 sm:px-6 py-2.5 sm:py-3 bg-[linear-gradient(135deg,_#f9cd4f_0%,_#d4a012_100%)] text-[#111] rounded-xl shadow-[0_0_24px_rgba(249,205,79,0.25)] hover:brightness-95 transition-all duration-300 font-medium whitespace-nowrap"
        >
          {displayAddress
            ? `${displayAddress.slice(0, 4)}...${displayAddress.slice(-3)}`
            : "Connect Wallet"}
        </button>
        <button
          onClick={() => setIsOpen(true)}
          className="h-10 sm:h-11 rounded-[10px] px-2 sm:px-4 flex items-center gap-1 sm:gap-2 border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.07)] transition-colors duration-200 min-w-0"
        >
          <Wallet className="h-4 w-4 text-[#f9cd4f]" />
          <span className="hidden sm:inline text-sm font-medium text-[#f9cd4f]">
            {isSyncing ? "Syncing..." : formattedPortfolioValue}
          </span>
          <span className="text-xs sm:text-sm text-[#aaa]">Portfolio</span>
        </button>
        <button
          onClick={handleLogout}
          aria-label="Disconnect wallet"
          className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-xl border border-white/15 bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white transition-colors duration-200 flex items-center justify-center"
        >
          <LogOutIcon className="h-4 w-4" />
        </button>
        </div>
      </>
    );
  }

  return (
    <button
      onClick={() => openModal()}
      className="w-full text-base md:text-xl px-6 py-3 bg-[linear-gradient(135deg,_#f9cd4f_0%,_#d4a012_100%)] text-[#111] rounded-xl shadow-[0_0_24px_rgba(249,205,79,0.25)] hover:brightness-95 transition-all duration-300 font-medium"
    >
      Connect Wallet
    </button>
  );
}
